import {
    Employee,
    EmployeeList,
    GetEmployeesWithActivePeriodQuery,
    GetWorkingEventListRequest,
    JsonPatchOperation,
    MatchEmployeeRequest,
    MatchEmployeeResponse,
    MergeRelativeRequest,
    MergeRelativeResponse,
    PatchRelativeResponse,
    StaffClient,
    WorkingEventList,
} from "@skbkontur/staff-common";
import { EmployeeListInitialState } from "@skbkontur/staff-selector-widget";
import { Toast } from "@skbkontur/react-ui";

import {
    CombinatorType,
    CombineFufResult,
    FufCombineMethod,
} from "../../../../Common/CandyApiClient/Types/DraftActions/CombineFufResult";
import type { Step } from "../../Controls/FormParts/Tour/TourTypes";
import { UnitId } from "../../Controls/FormParts/Unit/UnitId/UnitId";
import type { ServerErrorLog } from "../../../../Common/CandyApiClient/Types/ErrorLog/ServerErrorLog";
import type { BindingPath, ModelValue } from "../../Engine/Core/CoreTypes";
import type { ExternalHookFunction } from "../../Engine/Core/EventManagement/ExternalEvents/ExternalHookFunction";
import { ModelChunk } from "../../Engine/Core/ModelStoring/ModelChunk";
import { ModelHunk } from "../../Engine/Core/ModelStoring/ModelHunk";
import type { PicklistDataItem, PicklistQueryResponse } from "../../Engine/Core/Picklists/PicklistTypes";
import { CalculationAwaitingOption, IForm } from "../../Engine/Form/IForm";
import {
    closeCustomErrorLogTooltipAction,
    openCustomErrorLogTooltipAction,
} from "../../Controls/FormParts/EmbeddedLog/ErrorList/ErrorLogTooltip/CustomErrorLogTooltipAction";
import { nextTick } from "../../../../Common/AsyncUtils";
import { currentViewModelSelect } from "../../Helpers/StateAccessors/ViewModelSelect";
import { ModelEventsListener } from "../../Engine/Core/EventManagement/ModelEvents/ModelEvent";
import { DigestQueryFilter } from "../../../../Common/CandyApiClient/DigestApiClient/Types/DigestQueryFilter";
import { IUpdateFormModelAccessor } from "../../Engine/Form/IFormModelAccessor";
import { ModalOptions } from "../../Engine/Core/ModalManager/IModalState";
import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    convertToAbsoluteModelPath,
    convertToLegacyPath,
    createAbsolute,
    createAbsoluteFromMask,
    createModelPath,
    PathTokens,
} from "../../../../Common/ModelPath/ModelPath";
import { copySubtree } from "../../Engine/ActionCreators/AllActionCreators";
import {
    formCallInitSection,
    formOpenEmployeeEditor,
} from "../../Engine/Core/EventManagement/ExternalEvents/ExternalEvents";
import { addPageAction } from "../../Engine/ActionCreators/UnitActionCreators";
import { MassActionParameters } from "../../../../Common/CandyApiClient/Types/MassActions/MassActionParameters";
import { PrintType } from "../../../../Common/CandyApiClient/Types/Print/PrintType";
import { ImportEmployeesOptions } from "../../../../Common/CandyApiClient/Types/Staff/ImportEmployeesOptions";
import { Subscription } from "../../Engine/Form/ModelChangeEventListener";
import { FiasApi, FiasClientInitOptions } from "../../Controls/ValueEditors/Fias/FiasInput/FiasApi";
import { InstancesDeletingRule } from "../../../../Common/CandyApiClient/Types/InstancesDeleting/InstancesDeletingRule";
import { showZeroReportModalActionCreator } from "../../Engine/ActionCreators/ZeroReportActionCreator";
import { IFSPrinterClient } from "../../../../Common/FSPrinterClient/IFSPrinterClient";
import { loadInner } from "../FormInitialization/DataFetching";
import { PathLoadSpecification } from "../../../../Common/CandyApiClient/ICandyApiClient";
import { ICandyComponentHandle } from "../../Engine/Core/ComponentHandleRegistry/ICandyComponentHandle";
import { NavigationPathMapper } from "../InjectableModules/InjectableEngineModules";
import {
    EmployerNormalizedTag,
    ImportEmployerDocumentsOptions,
    ImportFromEmployeeServiceOptions,
} from "../../StaffServiceIntegration/EmployeeServiceTypes";
import { UIUtil } from "../../Engine/Form/CustomizationContexts/HelperFunctionContext/UIUtil/UIUtil";
import { getCandyHub } from "../Sentry/InitializeSentry";
import { TabInfosState } from "../../Controls/MultiControls/Tabs/Store/TabInfosState";
import { isNotNullOrUndefined, reject } from "../../../../Common/TypingUtils";
import {
    getImportArguments,
    importDocumentOptionsHasRequestsTags,
} from "../../StaffServiceIntegration/StaffServiceHelpers";
import { EmployeeRelativesActionType } from "../../StaffServiceIntegration/Relatives/State/EmployeeRelativesAction";
import { MetricsPayload, track } from "../../Controls/Internals/Metrics/MetricsProvider";
import { buildDirtyPathForMultiplicityAndCheckboxPath } from "../../Controls/FastSelectAllCheckboxesHelper";
import { OperatorType } from "../../Helpers/FilterExpressions/FilterExpression";
import { clearFormHandler } from "../../Engine/ActionCreators/FormActionCreators";
import { IMultilineManagerClient } from "../../Engine/Core/Filtering/MultilineManager";
import { waitPollingTask } from "../../../../Common/CandyApiClient/Types/PollingTasks/PollingTaskWaiter";
import { IContractorsApiClient } from "../../ContractorsServiceIntegration/ContractorsApiClient";
import {
    GetContractorResponse,
    ImportContractorsWidgetOptions,
} from "../../ContractorsServiceIntegration/Types/ContractorsTypes";
import {
    DataFieldRow,
    DataFieldValue,
} from "../../../../Common/CandyApiClient/Types/QueryCollecton/InnerQueryResponse";
import { AggregationType } from "../../Engine/Core/Filtering/IAggregationQueryStrategy";
import { RelativeModelFieldPath } from "../../../../Common/ModelPath/RelativeModelFieldPath";
import { iterateModelEntries } from "../../Controls/viewModelUtils";
import { HighlightedControl } from "../../Controls/ExternalHighlighting";

import { CallInitSectionLightboxProps, InitSectionFormLightboxOptions } from "./InitSectionLightbox";
import type { IPublicForm } from "./IPublicForm";
import { ModelChangeListener } from "./ModelChangeListener";

function mapChunkValues(chunk: ModelChunk): Array<{ [path: string]: ModelValue }> {
    return Iterator.from(ModelHunk.iteratePaths(chunk))
        .filter(
            path => ModelHunk.hasPath(chunk, path) && (ModelHunk.get(chunk, path)?.hasOwnProperty("value") ?? false)
        )
        .map(path => ({ [path]: ModelHunk.get(chunk, path)?.value }))
        .toArray();
}

function reduceChunkValues(chunks: Array<{ [path: string]: ModelValue }>): { [path: string]: ModelValue } {
    return chunks.reduce(
        (result, chunk) => ({
            ...result,
            ...chunk,
        }),
        {}
    );
}

class PublicForm implements IPublicForm {
    private readonly form: IForm;
    public readonly fsPrinterClient: IFSPrinterClient;
    private readonly pathMapper: NavigationPathMapper;

    public constructor(form: IForm) {
        this.form = form;
        this.fsPrinterClient = form.fsPrinterClient;
        this.pathMapper = form.navigationPathMapper;
    }

    public __INTERNAL__importFromEmployeeService(
        options?: ImportFromEmployeeServiceOptions,
        initialState?: EmployeeListInitialState
    ): Promise<Employee[] | undefined> {
        return this.form.importFromEmployeeService(options, initialState);
    }

    public async importEmployerDocuments(
        options: ImportEmployerDocumentsOptions
    ): Promise<EmployerNormalizedTag | undefined | null> {
        if (!options.gfv) {
            reject("GFV is empty");
        }
        if (!options.title) {
            reject("Title is empty");
        }
        if (!importDocumentOptionsHasRequestsTags(options)) {
            reject("RequestsTags are empty or incorrect. Must be string[][]");
        }

        const tag = await this.form.importEmployerDocument(options);
        if (!tag) {
            return undefined;
        }

        const fufUrl: string[] = [tag.content?.report.link ?? ""];
        const importArguments = getImportArguments(options, tag, this.form.store.getState().employerDocuments);

        const uiUtil = new UIUtil(this.form.settings.theme);
        await uiUtil.executeLongOperation(
            { title: "Импорт документов", modalSpecialCase: "InfiniteProcess", allowCancel: false },
            async progress => {
                try {
                    await this.save();
                    const response = await this.form.candyApiDraftClient.importXmls(
                        fufUrl,
                        FufCombineMethod.Merge,
                        CombinatorType.Default,
                        false,
                        importArguments
                    );
                    progress.reportProgress(100);
                    await this.reloadInner();
                    await uiUtil.mergeResult({
                        mergeResult: response,
                        documentName: options.importResultDocumentNameGenitive,
                    });
                } catch (e) {
                    Toast.push("Ошибка импорта");
                    // eslint-disable-next-line no-console
                    console.error(e);
                    getCandyHub().captureException(e);
                }
            }
        );

        return tag;
    }

    public async mergeRelative(
        bindingPath: BindingPath,
        employeeId: string,
        query: MergeRelativeRequest
    ): Promise<MergeRelativeResponse | undefined> {
        return this.form.mergeRelative(bindingPath, employeeId, query);
    }

    public patchRelative(
        employeeId: string,
        relativeId: string,
        operations: JsonPatchOperation[]
    ): Promise<PatchRelativeResponse> {
        return this.form.patchRelative(employeeId, relativeId, operations);
    }

    public matchEmployee(query: MatchEmployeeRequest): Promise<MatchEmployeeResponse> {
        return this.form.matchEmployee(query);
    }

    public cancelSelectRelative(bindingPath: BindingPath): void {
        this.form.store.dispatch({
            type: EmployeeRelativesActionType.SelectRelative,
            payload: {
                path: bindingPath,
                relative: null,
            },
        });
    }

    public getWorkingEventsForEmployee(query: GetWorkingEventListRequest): Promise<WorkingEventList | undefined> {
        return this.form.getWorkingEventsForEmployee(query);
    }

    public getStaffClient(): StaffClient {
        return this.form.staffClient.getStaffClient();
    }

    public getContractorsClient(): IContractorsApiClient {
        return this.form.contractorsClient;
    }

    public importContractorsWidgetOptions(
        options: ImportContractorsWidgetOptions
    ): Promise<GetContractorResponse[] | undefined> {
        return this.form.importContractorsWidgetOptions(options);
    }

    public sendMetrics(action: string, payload?: MetricsPayload): void {
        track("candy", action, payload);
    }

    public createFiasApiClient(opts?: FiasClientInitOptions): FiasApi {
        return new FiasApi(
            this.form.settings.keformsUrl,
            opts?.maxItems ?? 8,
            opts?.division ?? "administrative",
            opts?.addressLevel ?? "room"
        );
    }

    public getAdditionalEmployeeInfo(query: GetEmployeesWithActivePeriodQuery): Promise<EmployeeList | undefined> {
        return this.form.getAdditionalEmployeeInfo(query);
    }

    public getEmployees(query: GetEmployeesWithActivePeriodQuery): Promise<EmployeeList> {
        return this.form.getEmployees(query);
    }

    public importEmployees(employees: Employee[], options?: ImportEmployeesOptions): Promise<void> {
        return this.form.importEmployees(employees, options);
    }

    public runMassAction(actionId: string, parameters?: MassActionParameters): Promise<void> {
        return this.form.runMassAction(actionId, parameters);
    }

    public getByGId(gId: string, filter?: DigestQueryFilter): Promise<PicklistQueryResponse> {
        return this.form.picklistsProvider.getDigestApiPicklist(gId).query(filter);
    }

    public async waitForPendingCalculations(calculationAwaitingOption?: CalculationAwaitingOption): Promise<void> {
        await this.form.waitForPendingCalculations(calculationAwaitingOption);
    }

    public on(eventName: string, listener: ExternalHookFunction): void {
        this.form.onExternalEvent(["form", eventName], listener);
    }

    public off(eventName: string, listener: ExternalHookFunction): void {
        this.form.offExternalEvent(["form", eventName], listener);
    }

    public onBeforeModelChange(path: string | string[], listener: ModelChangeListener): Subscription {
        return this.form.onBeforeModelChange(path, listener);
    }

    public onModelChange(path: string | string[], listener: ModelChangeListener): Subscription {
        return this.form.onModelChange(path, listener);
    }

    public save(): Promise<void> {
        return this.form.changeSet.send();
    }

    public runCheck(): Promise<void> {
        return this.form.runCheckFromPublicForm();
    }

    public close(): Promise<void> {
        this.form.changeSet.stopAutosend();
        this.form.unmount?.();
        return this.form.changeSet.send();
    }

    public getTourSteps(): Step[] {
        // For old keweb
        return [];
    }

    public validate(): Promise<void> {
        const keys = ModelHunk.paths(this.form.getBatch(this.form.settings.formRoot));
        return this.form.revalidatePaths(keys.map(convertToAbsoluteModelPath));
    }

    public async validateChildrenByPath(path: string): Promise<void> {
        const absoluteModelPath = createAbsoluteFromMask(path, PathTokens.each);
        const iterableChildrenPaths = this.form.getChildrenPaths(absoluteModelPath);
        await this.form.revalidatePaths(iterableChildrenPaths);
    }

    public async focusFirstInvalidControl(): Promise<boolean> {
        return this.form.focusFirstInvalidControl();
    }

    public hasErrors(): boolean {
        const errorCount = this.form.getViewModel(this.form.settings.formRoot)?.errorsCount;
        return errorCount != undefined && errorCount > 0;
    }

    public hasErrorsForElba(): boolean {
        return (
            Number(
                currentViewModelSelect.customFieldValue(
                    this.form.store.getState(),
                    this.form.settings.formRoot,
                    "$errorCount"
                )
            ) > 0
        );
    }

    public onErrorsCountChanged(listener: ModelEventsListener): void {
        this.form.onErrorsCountChanged(listener);
    }

    public offErrorsCountChanged(listener: ModelEventsListener): void {
        this.form.offErrorsCountChanged(listener);
    }

    public async addInstance(path: BindingPath): Promise<string> {
        return this.form.addInstance(path);
    }

    public async removeInstance(path: BindingPath, instance: string): Promise<void> {
        this.form.removeInstance(path, instance);
    }

    public async removeInstances(path: BindingPath, instances: string[]): Promise<void> {
        this.form.removeInstances(path, instances);
    }

    public async batchUpdate(updateProc: (updateAccessor: IUpdateFormModelAccessor) => void): Promise<void> {
        return this.form.updateModel(updateProc);
    }

    public set(pathOrModelChunk: { [path: string]: string | undefined }): void | Promise<void>;
    public set(pathOrModelChunk: string, value: ModelValue): void | Promise<void>;
    public set(
        pathOrModelChunk: string | { [path: string]: string | undefined },
        value?: ModelValue
    ): void | Promise<void> {
        if (
            pathOrModelChunk !== null &&
            typeof pathOrModelChunk !== "string" &&
            typeof pathOrModelChunk === "object" &&
            !Array.isArray(pathOrModelChunk)
        ) {
            const modelHunk = ModelHunk.create();
            for (const path of Object.keys(pathOrModelChunk)) {
                ModelHunk.set(modelHunk, path, { value: pathOrModelChunk[path] });
            }
            this.form.setBatch(modelHunk);
            return;
        }

        if (typeof pathOrModelChunk === "string") {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.form.setValue(pathOrModelChunk, value);
            return;
        }

        throw new Error("form.set() parameter should be string path or object with path-value pairs");
    }

    public getChildren(path: BindingPath): string[] {
        return this.form.getChildren(path) ?? [];
    }

    public getPicklistData(path: BindingPath): Promise<PicklistDataItem> {
        return Promise.resolve(this.form.getViewModelField(path, "picklist"));
    }

    public getDisplayedInstances(path: BindingPath, filterUniqueKey: string): readonly string[] {
        const multilineManager = this.form.filteringManager.getMultilineManager(
            convertToAbsoluteModelPath(path),
            filterUniqueKey
        );
        return multilineManager.getCurrentState().displayedInstances;
    }

    public get(pathOrArray: string | string[]): { [path: string]: ModelValue } {
        if (Array.isArray(pathOrArray)) {
            return reduceChunkValues(
                pathOrArray
                    .map(path => mapChunkValues(this.form.getBatch(path)))
                    .reduce<Array<{ [path: string]: ModelValue }>>((result, array) => result.concat(array), [])
            );
        }

        if (typeof pathOrArray === "string") {
            return reduceChunkValues(mapChunkValues(this.form.getBatch(pathOrArray)));
        }

        if (pathOrArray === undefined) {
            return reduceChunkValues(mapChunkValues(this.form.getBatch(this.form.settings.formRoot)));
        }
        throw new Error("form.get() parameter should be string path or array of paths");
    }

    public getValue(path: string): ModelValue {
        return this.form.getValue(path);
    }

    public setErrorLog(errorLog: Partial<ServerErrorLog>): void {
        this.form.setErrorLogForTestsOnly({
            errors: [],
            warnings: [],
            successes: [],
            ...errorLog,
        });
    }

    public copySubtree(
        sourceBindingPath: BindingPath | AbsoluteResolvedModelPath,
        targetBindingPath: BindingPath | AbsoluteResolvedModelPath,
        doNotCopyPaths?: string[]
    ): void {
        copySubtree(this.form, sourceBindingPath, targetBindingPath, doNotCopyPaths);
    }

    public async reloadInner(): Promise<void> {
        await this.form.reloadInner();
    }

    public navigate(navigationPath: string): Promise<ICandyComponentHandle | undefined> {
        return this.form.activeLogHelper.navigate(navigationPath);
    }

    public navigateToFirstInvalidPath(): Promise<ICandyComponentHandle | undefined> {
        const firstViewModelWithError = Iterator.from(iterateModelEntries(this.form.store.getState().viewModel)).find(
            ([_, viewModel]) => Boolean(viewModel.error?.[0])
        );

        if (firstViewModelWithError) {
            const firstPathWithError = firstViewModelWithError[0].toLegacyPath();
            return this.form.activeLogHelper.navigate(firstPathWithError);
        }

        return Promise.resolve(undefined);
    }

    public unit(id: string, instance?: string): UnitController {
        return new UnitController(this.form, { pageId: id, instance: instance });
    }

    public async focusControlAndShowErrorMessage(path: BindingPath, errorMessage: string): Promise<void> {
        // Упражнение с закрытием тултипа и nextTick нужно для того, чтобы тултип закрылся и сбросил свое состяние
        this.form.store.dispatch(closeCustomErrorLogTooltipAction());
        await nextTick();
        const control = await this.form.activeLogHelper.navigate(path);
        const anchorElement = control?.getAnchorElement();
        if (anchorElement != undefined) {
            this.form.store.dispatch(openCustomErrorLogTooltipAction(anchorElement, errorMessage));
        }
    }

    public focusOnControlIfExist(path: BindingPath): boolean {
        const control = this.form.componentHandleRegistry.getComponentHandles(path)?.[0];
        control?.focus(convertToAbsoluteModelPath(path).toString());
        return control != undefined;
    }

    public navigateByHighlanderPaths(paths: BindingPath[]): Promise<undefined | ICandyComponentHandle> {
        return this.form.activeLogHelper.navigateByHighlanderPaths(paths);
    }

    public highlightControlsByPaths(paths: Array<HighlightedControl<string>>): void {
        const resolvedPath = (path: string) =>
            this.pathMapper.isCandyPath(path)
                ? createAbsoluteFromMask(path, PathTokens.each)
                : createAbsolute(this.pathMapper.mapPath(path));
        const resolvedPaths: Array<HighlightedControl<AbsoluteModelPath>> = paths.map(path => ({
            pathMask: resolvedPath(path.pathMask),
            highlightingType: path.highlightingType,
            tooltipText: path.tooltipText,
            isTooltipFixed: path.isTooltipFixed,
        }));
        this.form.setExternalHighlightedPaths(resolvedPaths);
    }

    public unhighlightControls(): void {
        this.form.setExternalHighlightedPaths([]);
        this.form.focusOnHighlightedControl(undefined);
    }

    public async focusOnHighlightedControl(path: string): Promise<void> {
        const resolvedPath = this.pathMapper.isCandyPath(path)
            ? path.startsWith("/")
                ? path.slice(1)
                : path
            : this.pathMapper.mapPath(path);
        this.form.focusOnHighlightedControl(createAbsolute(resolvedPath));

        if (!this.focusOnControlIfExist(resolvedPath)) {
            //на пути со * навигация не работает, поэтому пробуем снавигироваться к первому элементу во множественности
            await this.navigate(path.replaceAll("*", "0"));
        }
    }

    public getNavigationPathMapper(): NavigationPathMapper {
        return this.form.navigationPathMapper;
    }

    public callInitSection(options: CallInitSectionLightboxProps): void {
        this.form.triggerExternalEvent(formCallInitSection(options));
    }

    public openEmployeeEditor(employeeId: string): void {
        this.form.triggerExternalEvent(formOpenEmployeeEditor(employeeId));
    }

    public uuidGenerate(): string {
        return crypto.randomUUID();
    }

    public async __INTERNAL__getSelectFunctionForDisableEmployeeInSS(pathSpec: string) {
        const modelPath = createAbsoluteFromMask(pathSpec, PathTokens.each);
        const pathLoadSpecification: readonly PathLoadSpecification[] = [{ pathOrPathPrefix: modelPath, skip: false }];
        const values = await loadInner(this.form.candyApiDraftClient, true, pathLoadSpecification);
        const result = new Set<string>();
        Iterator.from(values.iterateEntries()).forEach(([_, viewModel]) => {
            viewModel?.value && result.add(viewModel.value);
        });
        return (employee: Employee) => (employee?.profile?.snils ? result.has(employee.profile.snils) : false);
    }

    public async addSection(options: InitSectionFormLightboxOptions): Promise<void> {
        if (options.initPage) {
            this.form.store.dispatch(addPageAction(options.initPage));
        }
        if (options.periodPath && options.period) {
            const mergedPeriodPath = createAbsoluteFromMask(options.path, PathTokens.each).joinWith(options.periodPath);
            await this.set(mergedPeriodPath.toString(), JSON.stringify(options.period));
        }
        if (options.zeroReporting) {
            await this.form.changeSet.send();
            this.form.store.dispatch(showZeroReportModalActionCreator());
        }
        if (options.setToPathTrue) {
            await this.set(options.path, "true");
        }
    }

    public showModal(options: ModalOptions): void {
        this.form.showCustomModal(options);
    }

    public async setAutoFieldsToAutoValue(pathPrefixes: string[]): Promise<void> {
        await this.form.customNormalizationHelper.fillAutoFieldsWithAutoValuesAndReload(
            pathPrefixes.map(createAbsolute)
        );
    }

    public async ensureInstanceFullyLoaded(
        bindingPath: BindingPath | AbsoluteResolvedModelPath,
        ...instances: string[]
    ): Promise<void> {
        await this.form.lazyLoadingManager.requestFullInstanceLoad(
            convertToAbsoluteModelPath(bindingPath),
            ...instances
        );
    }

    public async customNormalize(normalizerId: string, pathPrefixes: string[]): Promise<void> {
        await this.form.candyApiDraftClient.startCustomNormalizationTask(
            pathPrefixes.map(createAbsolute),
            normalizerId
        );
        await waitPollingTask(() => this.form.candyApiDraftClient.getCustomNormalizationTask());
    }

    public async print(type: PrintType, body?: string[]): Promise<void> {
        const { resultUri } = await this.form.candyApiDraftClient.print(type, body);
        window.open(resultUri, "_blank", "noopener,noreferrer")?.focus();
    }

    public removeInstancesOnServer(
        multiplicityPath: BindingPath,
        instances: string[],
        prefixes?: string[]
    ): Promise<void> {
        return this.removeInstancesByRules([
            { multiplicityPath: multiplicityPath, instanceNumbers: instances, prefixesToDelete: prefixes },
        ]);
    }

    public getTabsState(): TabInfosState {
        return this.form.store.getState().tabInfos;
    }

    public getModalUnitOpenedInstance(pageId: string): string | undefined | null {
        return this.form.store.getState().modalUnits?.find(x => x.modalUnitId.pageId === pageId)?.modalUnitId.instance;
    }

    public removeInstancesByRules(rules: InstancesDeletingRule[]): Promise<void> {
        return this.form.candyApiDraftClient.deleteInstances(rules);
    }

    public createDnp(): Promise<CombineFufResult | undefined> {
        return this.form.candyApiDraftClient.importXmls([], FufCombineMethod.Merge, CombinatorType.Default, false, [
            "mode_dnp",
        ]);
    }

    public getSelectedInstances(
        multiplicityPath: BindingPath | AbsoluteResolvedModelPath,
        tableUniqId: string,
        pathToChecked: string,
        checkedValue: string = "true"
    ): Promise<string[]> {
        const multilineManager = this.form.filteringManager.getMultilineManager(
            convertToAbsoluteModelPath(multiplicityPath),
            tableUniqId
        );
        const displayedInstances = multilineManager.getCurrentState().displayedInstances;
        const useServerFilter = multilineManager.getCurrentState().useServerFilter;
        const multiplicityPathTyped = convertToAbsoluteModelPath(multiplicityPath);
        const pathToCheckedTyped = createModelPath(pathToChecked, false);
        const pathToDirtyCheckboxesArrayPath = buildDirtyPathForMultiplicityAndCheckboxPath(
            multiplicityPathTyped,
            pathToCheckedTyped
        );
        const pathToDirtyCheckboxesArray = this.form.getValue(pathToDirtyCheckboxesArrayPath);

        const getCheckedInstancesLocally = () =>
            Promise.resolve(
                displayedInstances.filter(
                    instance =>
                        this.form.getValue(multiplicityPathTyped.joinWith(instance).joinWith(pathToCheckedTyped)) ===
                        checkedValue
                )
            );

        const getCheckedInstancesViaServer = async () => {
            const filteredInstances = await multilineManager.requestInstancesWithExtraFilter({
                operator: OperatorType.Equal,
                condition: {
                    dataPath: convertToLegacyPath(pathToCheckedTyped),
                    value: checkedValue,
                },
            });
            return filteredInstances;
        };

        const getCheckedInstancesByDirtyArray = () => {
            const dirtyArrayControlModelSet = JSON.parse(pathToDirtyCheckboxesArray ?? reject()) as string[];
            const displayedInstancesSet = new Set(displayedInstances);
            return Promise.resolve(dirtyArrayControlModelSet.filter(x => displayedInstancesSet.has(x)));
        };

        const result = isNotNullOrUndefined(pathToDirtyCheckboxesArray)
            ? getCheckedInstancesByDirtyArray()
            : useServerFilter
              ? getCheckedInstancesViaServer()
              : getCheckedInstancesLocally();

        return result;
    }

    public getSelectedQueryCollection(
        multiplicityPath: BindingPath | AbsoluteResolvedModelPath,
        requestPaths: string[],
        tableUniqId: string,
        pathToChecked: string,
        checkedValue: string = "true"
    ): Promise<DataFieldRow[]> {
        const multilineManager = this.form.filteringManager.getMultilineManager(
            convertToAbsoluteModelPath(multiplicityPath),
            tableUniqId
        );
        const multiplicityPathTyped = convertToAbsoluteModelPath(multiplicityPath);
        const pathToCheckedTyped = createModelPath(pathToChecked, false);

        const getCheckedItems = async () => {
            const filteredItems = await multilineManager.requestItemsWithExtraFilter(
                {
                    operator: OperatorType.Equal,
                    condition: {
                        dataPath: convertToLegacyPath(
                            multiplicityPathTyped.joinWith(PathTokens.each).joinWith(pathToCheckedTyped)
                        ),
                        value: checkedValue,
                    },
                },
                requestPaths.map(it => RelativeModelFieldPath.createFromString(it))
            );
            return filteredItems;
        };

        const displayedInstances = multilineManager.getCurrentState().displayedInstances;
        const pathToDirtyCheckboxesArrayPath = buildDirtyPathForMultiplicityAndCheckboxPath(
            multiplicityPathTyped,
            pathToCheckedTyped
        );
        const pathToDirtyCheckboxesArray = this.form.getValue(pathToDirtyCheckboxesArrayPath);
        const getCheckedItemsByDirtyArray = () => {
            const dirtyArrayControlModelSet = JSON.parse(pathToDirtyCheckboxesArray ?? reject()) as string[];
            const displayedInstancesSet = new Set(displayedInstances);
            return Promise.resolve(
                dirtyArrayControlModelSet
                    .filter(instance => displayedInstancesSet.has(instance))
                    .map(instance => [
                        instance,
                        ...requestPaths.map(path =>
                            this.form.getValue(multiplicityPathTyped.joinWith(instance).joinWith(path))
                        ),
                    ])
            );
        };

        const result = isNotNullOrUndefined(pathToDirtyCheckboxesArray)
            ? getCheckedItemsByDirtyArray()
            : getCheckedItems();

        return result;
    }

    public async getMaxValueFromQueryCollection(
        multiplicityPath: BindingPath | AbsoluteResolvedModelPath,
        tableUniqId: string,
        pathToValue: string
    ): Promise<DataFieldValue> {
        const multilineManager = this.form.filteringManager.getMultilineManager(
            convertToAbsoluteModelPath(multiplicityPath),
            tableUniqId
        );
        const result = await multilineManager.getAggregationValue(
            RelativeModelFieldPath.createFromString(pathToValue),
            AggregationType.Max
        );
        return result;
    }

    public getAttachmentPublicFormByPath(path: string): IPublicForm {
        const id = this.form.attachments.getAttachmentIdByPath(path);
        const attachmentPublicForm = this.form.getAttachmentsPublicFormsById(id);

        return attachmentPublicForm;
    }

    public getGfv() {
        return this.form.settings.gfv;
    }

    public async downloadFuf() {
        await this.form.changeSet.send();
        await this.form.normalizeDirtyMasksAndReloadInnerIfRequired();
        await this.form.candyApiDraftClient.downloadFuf();
    }

    public makeDownloadDriveLink(contentUri: string, filename?: string) {
        this.form.candyApiDraftClient.makeDownloadDriveLink(contentUri, filename);
    }

    public async clearFormWithReload() {
        await this.form.store.dispatch((d, s, form) => clearFormHandler(d, s, form));
    }

    public getMultilineManager(
        bindingPath: AbsoluteResolvedModelPath,
        filteringUniqueId: string = "default"
    ): IMultilineManagerClient {
        const client = this.form.filteringManager.getMultilineManager(bindingPath, filteringUniqueId);
        return client;
    }
}

class UnitController {
    readonly #form: IForm;
    public readonly unitId: UnitId;

    public constructor(form: IForm, unitId: UnitId) {
        this.#form = form;
        this.unitId = unitId;
    }

    public openModalUnit(unit: UnitController): void {
        this.#form.openModalUnit(this.unitId, unit.unitId);
    }

    public closeModalUnit(): void {
        this.#form.closeModalUnit(this.unitId);
    }
}

export function createPublicForm(form: IForm): IPublicForm {
    return new PublicForm(form);
}
